import React, {Dispatch, useCallback, useEffect} from "react";
import {Controller, FormProvider, useFieldArray, useForm, useFormContext} from "react-hook-form";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {showToast} from "common";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import _ from "lodash";
import {ESBA_DATE_FORMAT, LoadingSpinner, LOCAL_DATE_FORMAT} from "common";
import {AnticipatedDischargeInfo} from "./domain";
import {ReportModal} from "../form-components/ReportModal";
import DatePicker from "react-datepicker";
import {AnticipatedDischargePageAction} from "./AnticipatedDischargePage";
import {CustomDateInput} from "../../component/CustomDateInput";
import {parse, parseISO} from "date-fns";
import {ModalSelectionInput} from "../form-components/ModalSelectionInput";
import {YesNoOptionMap} from "../domain";

export type AnticipatedDischargeModalState = 'hide' | 'confirm' | 'update';

type Props = {
    dispatch: Dispatch<AnticipatedDischargePageAction>;
    state: AnticipatedDischargeModalState;
    modalSelectedItems: AnticipatedDischargeInfo[];
    onFinished: (formValues: ConfirmAnticipatedDischargeInputForm | null) => Promise<void>;
};

export function ConfirmedDischargeDateModal({state, dispatch, onFinished, modalSelectedItems}: Props) {
    const methods = useForm<ConfirmAnticipatedDischargeInputForm>({
        shouldUnregister: false
    });

    const {promiseInProgress} = usePromiseTracker();

    const submitWithTracking = useCallback((formValues: ConfirmAnticipatedDischargeInputForm | null) => trackPromise(onFinished(formValues)), [onFinished]);

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for Confirmed Discharge Date is cancelled`);
        methods.reset();
        dispatch({type: 'SET_DISCHARGE_MODAL_STATE', state: 'hide', selectedItems: []})
    }, [methods, dispatch]);

    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: ConfirmAnticipatedDischargeInputForm) => {
            data.action = state;
            await submitWithTracking(_.pick(data, [
                'action',
                'newAnticipatedDischargeDateDefined',
                'inputs',
            ]));
            methods.reset();
            dispatch({type: 'SET_DISCHARGE_MODAL_STATE', state: 'hide', selectedItems: []});
        })();
    }, [methods, dispatch, submitWithTracking]);


    return (
        <ReportModal visible={state !== 'hide'}
                     maxWidth={'max-w-5xl'}
                     title={state === 'confirm' ? 'Confirm Discharge Date' : 'Update Discharge Date'}
                     onClose={onCancel}>
            <FormProvider {...methods}>
                <form autoComplete="off" onSubmit={submit}>
                    {!promiseInProgress && <>
                        <div className={"p-2"}/>
                        {
                            state === 'update' &&
                            <ModalSelectionInput<ConfirmAnticipatedDischargeInputForm>
                                options={YesNoOptionMap}
                                label={"New Anticipated Discharge Date Defined"}
                                disabled={promiseInProgress}
                                field={'newAnticipatedDischargeDateDefined'}
                            />
                        }

                        <AnticipatedDischargeTable state={state}
                                                   loading={promiseInProgress}
                                                   items={modalSelectedItems}/>

                        <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                            <div className="flex w-1/4">
                                <FatOutlineButton color={'red'}
                                                  text={"Cancel"}
                                                  onClick={onCancel}/>
                                <span style={{width: 10}}/>
                                <FatOutlineButton type="submit" text={"Submit"} color={"green"}/>
                            </div>
                        </div>
                    </>
                    }

                    {promiseInProgress &&
                        <div className="p-12">
                            <LoadingSpinner text="Processing"/>
                        </div>
                    }
                </form>
            </FormProvider>
        </ReportModal>
    );
}

export type ConfirmAnticipatedDischargeInput = {
    sfId: string;
    clientName: string;
    clientSfId: string;
    clinicalRationale?: string;
    caseSfId: string;
    submittedAnticipatedDischargeDate: string;
}

export type ConfirmAnticipatedDischargeInputForm = {
    newAnticipatedDischargeDateDefined: string;
    action: string;
    inputs: ConfirmAnticipatedDischargeInput[];
}

type AnticipatedDischargeTableProps = {
    state: AnticipatedDischargeModalState
    loading: boolean;
    items: AnticipatedDischargeInfo[];
}

const AnticipatedDischargeTable = ({items, loading, state}: AnticipatedDischargeTableProps) => {
    const {control, formState, watch} = useFormContext<ConfirmAnticipatedDischargeInputForm>();
    const errors = formState.errors;
    const {fields, append, remove} = useFieldArray({
        control,
        name: "inputs"
    })

    const newAnticipatedDischargeDateDefined = watch('newAnticipatedDischargeDateDefined')
    const isConfirm = state === 'confirm';
    const shouldDisableAdInput = isConfirm || newAnticipatedDischargeDateDefined !== 'YES' || loading;
    const shouldDisableRationaleInput = isConfirm || loading ||
        (newAnticipatedDischargeDateDefined !== 'YES'
            && newAnticipatedDischargeDateDefined !== 'NO');


    useEffect(() => {
        remove();
        append(items.map((d, idx) => ({
            idx: idx,
            sfId: d.sfId,
            clientSfId: d.client.sfId,
            clientName: d.client.fullName,
            caseSfId: d.parentCase.sfId,
            clinicalRationale: d.clinicalRationale,
            submittedAnticipatedDischargeDate: parse(d.anticipatedDischargeDate, LOCAL_DATE_FORMAT, new Date()).toISOString()
        })));
    }, [remove, items, append, newAnticipatedDischargeDateDefined]);


    return (
        <div className={"p-4 overflow-auto flex flex-col flex-grow mb-4 mx-4 border-2 scrollbar-always"}
             style={{maxHeight: 600}}>
            <table className={'table table-fixed w-full'}>
                <thead className={'text-left select-none text-sm text-gray-portal-table-title'}>
                <tr>
                    <th className={'font-light'} colSpan={isConfirm ? 5 : 2}>Client Name</th>
                    <th className={'font-light'} colSpan={isConfirm ? 6 : 3}>Anticipated Discharge Date</th>
                    {
                        !isConfirm && <th className={'font-light'} colSpan={6}>Clinical Rationale</th>
                    }
                    <th colSpan={1}/>
                </tr>
                </thead>
                <tbody className={''}>
                {
                    fields.map((field, idx) => {
                            const hasErrorAd = errors[`inputs`]?.[idx]?.submittedAnticipatedDischargeDate !== undefined;
                            const hasErrorRd = errors[`inputs`]?.[idx]?.clinicalRationale !== undefined;
                            const hasError = hasErrorAd || hasErrorRd;
                            const shouldDisableRemove = fields.length === 1;


                            return (
                                <tr className={`border-b hover:bg-gray-200 ${hasError ? "bg-red-200 hover:bg-red-300" : ""}`}
                                    key={field.id}>
                                    <td className={'pl-4 py-4'} colSpan={isConfirm ? 5 : 2}>
                                        {field.clientName}
                                    </td>
                                    <td className={'py-4'} colSpan={isConfirm ? 6 : 3}>
                                        <Controller
                                            name={`inputs.${idx}.submittedAnticipatedDischargeDate`}
                                            control={control}
                                            rules={{required: {value: true, message: 'Required'}}}
                                            render={({field: {onChange, onBlur, value: isoValueStr}}) =>
                                                <div>
                                                    <DatePicker
                                                        disabled={shouldDisableAdInput}
                                                        onSelect={(newValue: Date | null) => {
                                                            onChange(newValue?.toISOString() || null);
                                                        }}
                                                        onChange={() => {}}
                                                        onBlur={onBlur}
                                                        selected={isoValueStr ? parseISO(isoValueStr) : undefined}
                                                        dateFormat={ESBA_DATE_FORMAT}
                                                        customInput={
                                                            <CustomDateInput
                                                                marginL={''}
                                                                fieldName={`inputs.${idx}.submittedAnticipatedDischargeDate`}
                                                                onFinish={(newValue: Date | null) => onChange(newValue?.toISOString() || null)}
                                                                value={new Date().toISOString()}
                                                            />
                                                        }
                                                    />
                                                    {hasErrorAd && <div
                                                        className="text-red-500 text-xs">{errors[`inputs`]?.[idx]?.submittedAnticipatedDischargeDate?.message}</div>}
                                                </div>
                                            }
                                        />
                                    </td>
                                    {
                                        !isConfirm &&

                                        <td className={'py-4'} colSpan={6}>
                                            <Controller
                                                name={`inputs.${idx}.clinicalRationale`}
                                                control={control}
                                                rules={{
                                                    required: {
                                                        value: true,
                                                        message: 'Required',
                                                    },
                                                    validate: {
                                                        max2048: (v: string | undefined) => {
                                                            return (v || '').length <= 2048 || 'Maximum length is 2048 characters';
                                                        }
                                                    }
                                                }}
                                                render={({field: {onChange, onBlur, value}}) =>
                                                    <div>
                                                    <textarea
                                                        onChange={onChange}
                                                        onBlur={onBlur}
                                                        disabled={shouldDisableRationaleInput}
                                                        value={value}
                                                        className={`w-full border block appearance-none text-black ${shouldDisableRationaleInput ? '' : 'bg-white'}
                                                        border-gray-400 leading-tight focus:outline-none text-sm p-1 rounded 
                                                        ${hasErrorRd ? 'border-red-500' : ''}`}
                                                        rows={2}
                                                    />
                                                        {hasErrorRd && <div
                                                            className="text-red-500 text-xs">{errors[`inputs`]?.[idx]?.clinicalRationale?.message}</div>}
                                                    </div>
                                                }
                                            />
                                        </td>
                                    }
                                    <td colSpan={1}>
                                        {shouldDisableRemove ? null :
                                            <button
                                                className={`text-xs underline text-red-500 cursor-pointer font-bold`}
                                                disabled={shouldDisableRemove}
                                                onClick={() => remove(idx)}>remove</button>
                                        }
                                    </td>
                                </tr>

                            )
                        }
                    )
                }
                </tbody>

            </table>
        </div>
    )
}

